import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  History,
  Loader2,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  bezi,
  useNahladObnovy,
  useObnovaStav,
  useObnovZoZalohy,
  usePripravZalohu,
  useSuboryZaloh,
  useUkonciObnovu,
  useZakazkyZalohy,
} from "@/api/hooks/useZalohy";
import type {
  DielecRozdiel,
  NahladObnovy,
  ObnovaStav,
  ObnovitVysledok,
  VariantaNahladObnovy,
} from "@/api/schemas";
import { useDebounce } from "@/lib/useDebounce";

/**
 * Obnova jednej zákazky zo zálohy (KOLO 198).
 *
 * User: *„chcel by som, aby tam bolo tlačidlo na obnovu zákaziek a to, že
 * si viem vybrať, ktorú obnoviť“*. Celý dump sa do živej databázy pustiť
 * nedá — prepísal by aj prácu po zálohe a vrátil sekvencie dozadu. Preto
 * kontajner `backup` obnoví zálohu do **dočasnej** databázy vedľa živej
 * a appka z nej vybranú variantu skopíruje do zákazky ako **novú**.
 * Nič sa neprepíše; riadky sa vrátia cez Ctrl+C / Ctrl+V.
 */

const V_APPKE: Record<string, string> = {
  existuje: "v appke",
  archiv: "v Archíve",
  zmazana: "zmazaná natrvalo",
};

/** `tandem_2026-10-20.sql.gz` → `20. 10. 2026` */
export function datumZalohy(subor: string): string {
  const m = /^tandem_(\d{4})-(\d{2})-(\d{2})\.sql\.gz$/.exec(subor);
  if (!m) return subor;
  return `${Number(m[3])}. ${Number(m[2])}. ${m[1]}`;
}

function cas(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("sk-SK", {
    day: "numeric",
    month: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function hodina(iso: string): string {
  return new Date(iso).toLocaleTimeString("sk-SK", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Ako záložka v editore: cenová „Výpočet A“, výrobná vlastným menom. */
function menoVarianty(nazov: string, jeVyrobna = false): string {
  return jeVyrobna ? nazov : `Výpočet ${nazov}`;
}

function velkost(bajtov: number): string {
  if (bajtov < 1024 * 1024) return `${Math.max(1, Math.round(bajtov / 1024))} kB`;
  return `${(bajtov / (1024 * 1024)).toFixed(1)} MB`;
}

export function ObnovaZakazky(): JSX.Element {
  const stavQ = useObnovaStav();
  const poz = stavQ.data?.data ?? null;
  // `zrusit` = Hotovo kliknuté, kontajner DB práve maže — pre usera je
  // to už koniec, môže pripraviť ďalšiu zálohu
  const aktivna = poz && poz.stav !== "zrusit" ? poz : null;

  return (
    <section className="space-y-4 rounded-lg border bg-card p-4">
      <div>
        <h2 className="flex items-center gap-2 text-xl">
          <History className="h-5 w-5 text-primary" />
          Obnoviť zákazku zo zálohy
        </h2>
        <p className="text-xs text-muted-foreground">
          Záloha sa pripraví do dočasnej databázy vedľa živej — nič sa
          neprepíše. Vybraná varianta pribudne do zákazky ako nová a riadky
          z nej skopíruješ cez Ctrl+C / Ctrl+V.
        </p>
      </div>

      {stavQ.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : !aktivna || aktivna.stav === "chyba" ? (
        <VyberZalohy chyba={aktivna} />
      ) : bezi(aktivna) ? (
        <Pripravuje poz={aktivna} />
      ) : (
        <Pripravena poz={aktivna} />
      )}
    </section>
  );
}

// ─── 1. Výber zálohy ─────────────────────────────────────────

function VyberZalohy({ chyba }: { chyba: ObnovaStav | null }): JSX.Element {
  const suboryQ = useSuboryZaloh();
  const priprav = usePripravZalohu();
  const subory = suboryQ.data?.data ?? [];

  async function pripravit(subor: string): Promise<void> {
    try {
      await priprav.mutateAsync(subor);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Zálohu sa nepodarilo pripraviť",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <div className="space-y-3">
      {chyba ? (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-medium text-destructive">
              Zálohu z {datumZalohy(chyba.subor)} sa nepodarilo pripraviť
            </p>
            {chyba.chyba ? (
              <p className="break-words font-mono text-[11px] text-muted-foreground">
                {chyba.chyba}
              </p>
            ) : null}
            <p className="text-xs text-muted-foreground">
              Skús inú zálohu. Živá databáza sa nezmenila.
            </p>
          </div>
        </div>
      ) : null}

      <p className="text-sm">Ktorú zálohu pripraviť?</p>
      {suboryQ.isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : subory.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Na serveri zatiaľ nie je žiadna záloha.
        </p>
      ) : (
        <ul className="max-h-72 divide-y overflow-y-auto rounded-md border">
          {subory.map((s) => (
            <li
              key={s.subor}
              className="flex items-center justify-between gap-3 px-3 py-2"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium">
                  {datumZalohy(s.subor)}
                </div>
                <div className="text-xs text-muted-foreground">
                  záloha o {hodina(s.vytvorena)} · {velkost(s.velkost_b)}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={priprav.isPending}
                onClick={() => void pripravit(s.subor)}
              >
                Pripraviť
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── 2. Príprava beží ────────────────────────────────────────

function Pripravuje({ poz }: { poz: ObnovaStav }): JSX.Element {
  const minut = (Date.now() - Date.parse(poz.created_at)) / 60_000;
  return (
    <div className="flex items-start gap-2 rounded-md border p-3">
      <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary" />
      <div className="space-y-1">
        <p className="text-sm font-medium">
          Pripravujem zálohu z {datumZalohy(poz.subor)}…
        </p>
        <p className="text-xs text-muted-foreground">
          {poz.stav === "caka"
            ? "Čaká, kým ju zálohovací kontajner prevezme (pár sekúnd)."
            : "Obnovuje sa do dočasnej databázy, zvyčajne do minúty."}
        </p>
        {poz.stav === "caka" && minut > 2 ? (
          <p className="text-xs text-destructive">
            Kontajner ju neprevzal ani po dvoch minútach — pravdepodobne
            nebeží. Na serveri: <code className="font-mono">docker compose
            logs --tail 50 backup</code>
          </p>
        ) : null}
      </div>
    </div>
  );
}

// ─── 3. Pripravená — zákazky a náhľad ────────────────────────

function Pripravena({ poz }: { poz: ObnovaStav }): JSX.Element {
  const [zakazkaId, setZakazkaId] = useState<string | null>(null);
  const hotovo = useUkonciObnovu();

  async function ukoncit(): Promise<void> {
    try {
      await hotovo.mutateAsync();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Nepodarilo sa ukončiť",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-emerald-600/30 bg-emerald-600/10 p-3">
        <div className="space-y-0.5">
          <p className="text-sm font-medium">
            Záloha z {datumZalohy(poz.subor)} je pripravená
          </p>
          <p className="text-xs text-muted-foreground">
            Posledná zmena v nej: {cas(poz.zaloha_do)}. Keď skončíš, klikni
            Hotovo — dočasná databáza sa zmaže (inak sama po 12 h).
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={hotovo.isPending}
          onClick={() => void ukoncit()}
        >
          Hotovo
        </Button>
      </div>

      {zakazkaId ? (
        <NahladZakazky
          poz={poz}
          zakazkaId={zakazkaId}
          onSpat={() => setZakazkaId(null)}
        />
      ) : (
        <ZoznamZakaziek poz={poz} onVyber={setZakazkaId} />
      )}
    </div>
  );
}

function ZoznamZakaziek({
  poz,
  onVyber,
}: {
  poz: ObnovaStav;
  onVyber: (id: string) => void;
}): JSX.Element {
  const [hladat, setHladat] = useState("");
  const hladatPomaly = useDebounce(hladat.trim(), 250);
  const q = useZakazkyZalohy(poz.id, hladatPomaly);
  const zakazky = q.data?.data ?? [];

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={hladat}
          onChange={(e) => setHladat(e.target.value)}
          placeholder="Hľadať zákazku v zálohe — číslo alebo názov"
          aria-label="Hľadať zákazku v zálohe"
          className="pl-8"
        />
      </div>
      {q.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : q.isError ? (
        <p className="text-sm text-destructive">
          {extractErrorMessage(q.error)}
        </p>
      ) : zakazky.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {hladatPomaly
            ? "V zálohe taká zákazka nie je."
            : "Záloha neobsahuje žiadnu zákazku."}
        </p>
      ) : (
        <ul className="max-h-96 divide-y overflow-y-auto rounded-md border">
          {zakazky.map((z) => (
            <li key={z.id}>
              <button
                type="button"
                onClick={() => onVyber(z.id)}
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-muted/60"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{z.nazov}</div>
                  <div className="text-xs text-muted-foreground">
                    {z.cislo} · {z.variant}{" "}
                    {z.variant === 1 ? "varianta" : z.variant < 5 ? "varianty" : "variant"}
                  </div>
                </div>
                <StitokVAppke v={z.v_appke} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StitokVAppke({ v }: { v: string }): JSX.Element {
  return (
    <Badge
      variant={v === "existuje" ? "secondary" : v === "zmazana" ? "destructive" : "outline"}
      className="shrink-0"
    >
      {V_APPKE[v] ?? v}
    </Badge>
  );
}

// ─── Náhľad jednej zákazky ───────────────────────────────────

function NahladZakazky({
  poz,
  zakazkaId,
  onSpat,
}: {
  poz: ObnovaStav;
  zakazkaId: string;
  onSpat: () => void;
}): JSX.Element {
  const q = useNahladObnovy(poz.id, zakazkaId);
  const n = q.data?.data;

  return (
    <div className="space-y-3">
      <Button size="sm" variant="ghost" className="-ml-2" onClick={onSpat}>
        <ArrowLeft className="mr-1 h-4 w-4" />
        Späť na zákazky v zálohe
      </Button>
      {q.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !n ? (
        <p className="text-sm text-destructive">
          {q.isError ? extractErrorMessage(q.error) : "Náhľad sa nenačítal."}
        </p>
      ) : (
        <Nahlad key={n.zakazka_id} n={n} />
      )}
    </div>
  );
}

function Nahlad({ n }: { n: NahladObnovy }): JSX.Element {
  const zmazana = n.v_appke === "zmazana";
  const archiv = n.v_appke === "archiv";
  // Predvolene aktívna varianta zo zálohy — tú zvyčajne niekto pokazil
  const [vybrane, setVybrane] = useState<string[]>(() => {
    const aktivna = n.varianty.find((v) => v.aktivna) ?? n.varianty[0];
    return aktivna ? [aktivna.id] : [];
  });
  const [vysledok, setVysledok] = useState<ObnovitVysledok | null>(null);
  const obnov = useObnovZoZalohy();

  function prepni(id: string): void {
    setVybrane((pred) =>
      pred.includes(id) ? pred.filter((x) => x !== id) : [...pred, id],
    );
  }

  async function obnovit(): Promise<void> {
    try {
      const { data } = await obnov.mutateAsync({
        zakazkaId: n.zakazka_id,
        // Zmazaná zákazka sa vracia celá — prázdny zoznam = všetky
        variantaIds: zmazana ? [] : vybrane,
      });
      setVysledok(data);
      toast({
        title: data.nova_zakazka ? "Zákazka obnovená" : "Varianta obnovená",
        description: data.varianty
          .map((v) => menoVarianty(v.nazov))
          .join(", "),
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Obnova zlyhala",
        description: extractErrorMessage(err),
      });
    }
  }

  const blokuje = n.chybajuce.length > 0;
  const pocet = vybrane.length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold">
          {n.cislo} · {n.nazov}
        </h3>
        <StitokVAppke v={n.v_appke} />
      </div>

      {n.posledna_zmena_v_zalohe ? (
        <p className="text-xs text-muted-foreground">
          Posledná zmena zákazky v zálohe: {cas(n.posledna_zmena_v_zalohe.cas)}{" "}
          — {n.posledna_zmena_v_zalohe.autor}: {n.posledna_zmena_v_zalohe.popis}
        </p>
      ) : null}

      <ZmenyPoZalohe n={n} />

      {archiv ? (
        <div className="rounded-md border bg-muted/40 p-3 text-sm">
          Zákazka je v Archíve — obnov ju tam (Zákazky → Archív → ⋮ →
          Obnoviť). Zo zálohy ju netreba.
        </div>
      ) : null}

      <div className="space-y-2">
        <p className="text-sm font-medium">
          {zmazana
            ? "Varianty v zálohe — vrátia sa všetky"
            : "Ktoré varianty obnoviť?"}
        </p>
        {n.varianty.map((v) => (
          <VariantaKarta
            key={v.id}
            v={v}
            vyber={zmazana || archiv ? null : vybrane.includes(v.id)}
            onPrepni={() => prepni(v.id)}
          />
        ))}
      </div>

      {blokuje ? (
        <div className="space-y-1 rounded-md border border-destructive/40 bg-destructive/10 p-3">
          <p className="text-sm font-medium text-destructive">
            Obnova nepôjde — v appke chýba, na čo sa záloha odvoláva:
          </p>
          <ul className="list-disc pl-5 text-xs">
            {n.chybajuce.map((c) => (
              <li key={`${c.druh}-${c.hodnota}`}>{c.popis}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {vysledok ? (
        <Vysledok vysledok={vysledok} />
      ) : archiv ? null : (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {zmazana
              ? "Zákazka sa vráti so všetkými variantami a cenami upravenými pre zákazku. Odoslania do Cut Rite, odpracovaný čas a objednávky sa nevracajú."
              : "Vybrané varianty pribudnú do zákazky ako nové cenové varianty „… — záloha“. Aktívna varianta ani výroba sa nemenia."}
          </p>
          <Button
            disabled={obnov.isPending || blokuje || (!zmazana && pocet === 0)}
            onClick={() => void obnovit()}
          >
            {obnov.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : null}
            {zmazana
              ? "Obnoviť ako novú zákazku"
              : pocet > 1
                ? `Obnoviť ako nové varianty (${pocet})`
                : "Obnoviť ako novú variantu"}
          </Button>
        </div>
      )}
    </div>
  );
}

function Vysledok({ vysledok }: { vysledok: ObnovitVysledok }): JSX.Element {
  // Obnovená varianta je vždy cenová (výroba sa nemení)
  const mena = vysledok.varianty
    .map((v) => `„${menoVarianty(v.nazov)}“`)
    .join(", ");
  return (
    <div className="flex items-start gap-2 rounded-md border border-emerald-600/30 bg-emerald-600/10 p-3">
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
      <div className="space-y-1 text-sm">
        <p className="font-medium">
          {vysledok.nova_zakazka
            ? "Zákazka je späť."
            : vysledok.varianty.length > 1
              ? `Do zákazky pribudli varianty ${mena}.`
              : `Do zákazky pribudla varianta ${mena}.`}
        </p>
        <p className="text-xs text-muted-foreground">
          {vysledok.nova_zakazka
            ? "Nájdeš ju v Zákazkách."
            : "Prepni sa na ňu v zákazke a čo treba, skopíruj do pôvodnej (Ctrl+C / Ctrl+V)."}
        </p>
        <Link
          to={`/zakazky/${vysledok.zakazka_id}`}
          className="text-sm font-medium text-primary underline-offset-2 hover:underline"
        >
          Otvoriť zákazku
        </Link>
      </div>
    </div>
  );
}

function ZmenyPoZalohe({ n }: { n: NahladObnovy }): JSX.Element {
  if (n.pocet_zmien_po_zalohe === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Po zálohe sa zákazka nemenila.
      </p>
    );
  }
  return (
    <div className="space-y-2 rounded-md border p-3">
      <p className="text-sm font-medium">
        Po zálohe zákazku menili ({n.pocet_zmien_po_zalohe}{" "}
        {n.pocet_zmien_po_zalohe === 1 ? "zmena" : n.pocet_zmien_po_zalohe < 5 ? "zmeny" : "zmien"})
      </p>
      <table className="text-xs">
        <thead className="text-muted-foreground">
          <tr className="text-left">
            <th className="py-0.5 pr-6 font-normal">Kto</th>
            <th className="py-0.5 pr-6 font-normal">Zmien</th>
            <th className="py-0.5 pr-6 font-normal">Prvá</th>
            <th className="py-0.5 font-normal">Posledná</th>
          </tr>
        </thead>
        <tbody>
          {n.autori_po_zalohe.map((a) => (
            <tr key={a.autor}>
              <td className="py-0.5 pr-6 font-medium">{a.autor}</td>
              <td className="py-0.5 pr-6 tabular-nums">{a.pocet}</td>
              <td className="py-0.5 pr-6">{cas(a.prva)}</td>
              <td className="py-0.5">{cas(a.posledna)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {n.zmeny_po_zalohe.length > 0 ? (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">
            Posledné zmeny ({n.zmeny_po_zalohe.length} z{" "}
            {n.pocet_zmien_po_zalohe})
          </summary>
          <ul className="mt-1 space-y-0.5">
            {n.zmeny_po_zalohe.map((z, i) => (
              <li key={i}>
                <span className="tabular-nums text-muted-foreground">
                  {cas(z.cas)}
                </span>{" "}
                <span className="font-medium">{z.autor}</span>: {z.popis}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function VariantaKarta({
  v,
  vyber,
  onPrepni,
}: {
  v: VariantaNahladObnovy;
  /** `null` = bez fajky (zmazaná zákazka sa vracia celá, archív vôbec) */
  vyber: boolean | null;
  onPrepni: () => void;
}): JSX.Element {
  const zmazana = v.v_appke === "zmazana";
  const rovnaka =
    !zmazana &&
    v.pocet_len_v_zalohe + v.pocet_len_teraz + v.pocet_zmenenych === 0 &&
    v.zostavy.every((z) => z.stav === "rovnaka");
  const zmeneneZostavy = v.zostavy.filter((z) => z.stav !== "rovnaka");

  return (
    <div className="rounded-md border p-3">
      <label className="flex items-start gap-2">
        {vyber !== null ? (
          <input
            type="checkbox"
            className="mt-1"
            checked={vyber}
            onChange={onPrepni}
            aria-label={`Obnoviť ${menoVarianty(v.nazov, v.je_vyrobna)}`}
          />
        ) : null}
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-medium">
              {menoVarianty(v.nazov, v.je_vyrobna)}
            </span>
            {v.aktivna ? <Badge variant="secondary">aktívna</Badge> : null}
            {v.je_vyrobna ? <Badge variant="outline">výrobná</Badge> : null}
            {zmazana ? (
              <Badge variant="destructive">v appke už nie je</Badge>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">
            V zálohe {v.zostav} {v.zostav === 1 ? "zostava" : v.zostav < 5 ? "zostavy" : "zostáv"}, {v.dielcov}{" "}
            {v.dielcov === 1 ? "riadok" : v.dielcov < 5 ? "riadky" : "riadkov"}
            {zmazana
              ? " — varianta sa vráti celá."
              : rovnaka
                ? " — rovnaká ako dnes."
                : ` — dnes inak: ${[
                    v.pocet_len_v_zalohe
                      ? `${v.pocet_len_v_zalohe} len v zálohe`
                      : "",
                    v.pocet_len_teraz ? `${v.pocet_len_teraz} pribudlo` : "",
                    v.pocet_zmenenych ? `${v.pocet_zmenenych} zmenených` : "",
                  ]
                    .filter(Boolean)
                    .join(", ") || "zostavy"}.`}
          </p>
        </div>
      </label>

      {!zmazana && !rovnaka ? (
        <details className="mt-2 text-xs">
          <summary className="cursor-pointer text-muted-foreground">
            Čo je inak
          </summary>
          <div className="mt-2 space-y-2">
            {zmeneneZostavy.length > 0 ? (
              <ZoznamRozdielov nadpis="Zostavy">
                {zmeneneZostavy.map((z) => (
                  <li key={z.kod + z.nazov}>
                    <span className="font-mono">{z.kod}</span> {z.nazov} —{" "}
                    {z.stav === "len_v_zalohe"
                      ? "len v zálohe (po zálohe zmazaná)"
                      : z.stav === "len_teraz"
                        ? "pribudla po zálohe"
                        : z.dielcov_zaloha === z.dielcov_teraz
                          ? "zmenená"
                          : `${z.dielcov_zaloha} → ${z.dielcov_teraz} riadkov`}
                  </li>
                ))}
              </ZoznamRozdielov>
            ) : null}
            <ZoznamDielcov
              nadpis="Len v zálohe — po zálohe zmazané"
              dielce={v.len_v_zalohe}
              pocet={v.pocet_len_v_zalohe}
            />
            <ZoznamDielcov
              nadpis="Len dnes — pribudli po zálohe"
              dielce={v.len_teraz}
              pocet={v.pocet_len_teraz}
            />
            <ZoznamDielcov
              nadpis="Zmenené po zálohe"
              dielce={v.zmenene}
              pocet={v.pocet_zmenenych}
            />
          </div>
        </details>
      ) : null}
    </div>
  );
}

function ZoznamRozdielov({
  nadpis,
  children,
}: {
  nadpis: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <p className="font-medium">{nadpis}</p>
      <ul className="space-y-0.5 pl-3">{children}</ul>
    </div>
  );
}

function ZoznamDielcov({
  nadpis,
  dielce,
  pocet,
}: {
  nadpis: string;
  dielce: DielecRozdiel[];
  pocet: number;
}): JSX.Element | null {
  if (pocet === 0) return null;
  return (
    <ZoznamRozdielov nadpis={`${nadpis} (${pocet})`}>
      {dielce.map((d, i) => (
        <li key={`${d.kod}-${i}`}>
          <span className="font-mono">{d.kod || d.zostava}</span> {d.nazov}
          {d.polia.length ? (
            <span className="text-muted-foreground"> — {d.polia.join(", ")}</span>
          ) : null}
        </li>
      ))}
      {pocet > dielce.length ? (
        <li className="text-muted-foreground">
          … a ďalších {pocet - dielce.length}
        </li>
      ) : null}
    </ZoznamRozdielov>
  );
}
